// Material UI Components
import Gate from "components/Gate";
import { useCan } from "modules/settings/accessRoles";
import { makeStyles } from "@mui/styles";
import {
  Button,
  ButtonGroup,
  Divider as MuiDivider,
  IconButton,
  TextField,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";

// React Components and Hooks
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

// Custom Components
import { EditableTable } from "../../components/EditableTable";
import StandardHeader from "../../components/global/StandardHeader";
import { LeftSidebar } from "layout-blueprints";

// Snackbar
import { setSnackbar } from "../../modules/snackbar/api/snackbar";
import {
  createVendor,
  get as getVendors,
  updateVendor,
} from "../../modules/vendor/api/actions";

// Modals
import EditVendor from "./modals/EditVendor";
import NewVendor from "./modals/NewVendor";

// Utils
import filterVendors from "./utils/filterVendors";

const Divider = styled(MuiDivider)(spacing);

const useStyles = makeStyles((theme) => ({
  root: {
    "& label.Mui-focused": {
      color: theme.palette.secondary.main,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: theme.palette.secondary.main,
    },
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: theme.palette.secondary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.secondary.main,
      },
    },
  },
  active: {
    fontWeight: "bold",
    color: "#f9a825",
    borderColor: "#f9a825",
  },
  inactive: {
    fontWeight: "bold",
  },
}));

const VendorsPage = () => {
  const canEditVendors = useCan()("editRecords");
  const classes = useStyles();
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [vendorsStatusFilter, setVendorsStatusFilter] = useState("Active");
  const [newVendorFormOpen, setNewVendorFormOpen] = useState(false);
  const [editVendorFormOpen, setEditVendorFormOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState({});
  const vendors = useSelector((state) => state.vendors.data.vendorsList.items);
  const vendorsLoading = useSelector((state) => state.vendors.isLoading);

  const vendorsToDisplay = filterVendors(
    vendors,
    searchQuery,
    vendorsStatusFilter
  );

  useEffect(() => {
    document.title = "Ubil+ Vendors";
    dispatch(getVendors()).catch(() => {
      dispatch(setSnackbar(true, "error", "Failed to fetch vendors."));
    });
  }, [dispatch]);

  const fieldTitles = [
    "Vendor #",
    "Name",
    "Service Territory",
    "Contact Name",
    "Status",
    "",
  ];
  const fieldTypes = [
    "labelSmall",
    "labelSmall",
    "labelSmall",
    "labelSmall",
    "labelSmall",
    "editBtn",
  ];
  const fieldNames = [
    "vendorNum",
    "name",
    "serviceTerritory",
    "contactName",
    "status",
    "",
  ];

  const handleSearchQueryInput = (event) => {
    const nextSearchQuery = String(event.target.value);
    setSearchQuery(nextSearchQuery);
  };

  const CurrentLookupTypeButton = () => {
    return (
      <IconButton>
        <SearchIcon mr={2} />
      </IconButton>
    );
  };

  const SearchBar = () => {
    return (
      <TextField
        id="searchTarget"
        label="Search Vendors"
        autoComplete="off"
        type="text"
        placeholder="Search by vendor number, name, contact, territory..."
        onChange={handleSearchQueryInput}
        inputRef={(text) => text && text.focus()}
        name="searchTarget"
        defaultValue={searchQuery}
        fullWidth
        size="small"
        variant="outlined"
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={{ endAdornment: <CurrentLookupTypeButton /> }}
      />
    );
  };

  const changeVendorsStatus = (val) => {
    setVendorsStatusFilter(val);
  };

  const handleVendorEditFormOpen = (item) => {
    if (!canEditVendors) return;
    setSelectedVendor(item);
    setEditVendorFormOpen(true);
  };

  const handleVendorRowClick = (item) => {
    if (item && item.uuid) {
      window.open("/vendor/" + item.uuid, "_blank");
    }
  };

  const handleNewVendorFormOpen = () => {
    setNewVendorFormOpen(true);
  };

  const handleNewVendorFormClose = () => {
    setNewVendorFormOpen(false);
  };

  const handleEditVendorFormClose = () => {
    setEditVendorFormOpen(false);
    setSelectedVendor({});
  };

  const submitNewVendor = async (vendorObj) => {
    try {
      await dispatch(createVendor(vendorObj));
      dispatch(setSnackbar(true, "success", "Successfully created vendor."));
      return true;
    } catch (err) {
      dispatch(setSnackbar(true, "error", "Failed to create vendor."));
      return false;
    }
  };

  const submitVendorEdit = async (originalVendorObj, editVendorObj) => {
    try {
      await dispatch(updateVendor(originalVendorObj.uuid, editVendorObj));
      dispatch(setSnackbar(true, "success", "Successfully edited vendor."));
      return true;
    } catch (err) {
      dispatch(setSnackbar(true, "error", "Failed to edit vendor."));
      return false;
    }
  };

  const Filters = () => {
    return (
      <ButtonGroup color="primary">
        <Button
          onClick={() => {
            changeVendorsStatus("All");
          }}
          className={
            vendorsStatusFilter === "All" ? classes.active : classes.inactive
          }
        >
          All
        </Button>
        <Button
          onClick={() => {
            changeVendorsStatus("Active");
          }}
          className={
            vendorsStatusFilter === "Active"
              ? classes.active
              : classes.inactive
          }
        >
          Active
        </Button>
        <Button
          onClick={() => {
            changeVendorsStatus("Inactive");
          }}
          className={
            vendorsStatusFilter === "Inactive"
              ? classes.active
              : classes.inactive
          }
        >
          Inactive
        </Button>
      </ButtonGroup>
    );
  };

  const toShowAll = {
    items: JSON.parse(JSON.stringify(vendorsToDisplay.items)),
    toUpdate: [],
    toDelete: [],
  };

  return (
    <LeftSidebar>
      <NewVendor
        open={newVendorFormOpen}
        handleClose={handleNewVendorFormClose}
        submitNewVendor={submitNewVendor}
      />
      <EditVendor
        open={editVendorFormOpen}
        handleClose={handleEditVendorFormClose}
        vendor={selectedVendor}
        submitVendorEdit={submitVendorEdit}
      />
      <StandardHeader title="Vendors Management" />
      <Divider className="mb-3 mt-3" />

      {/* Full Width Search Bar */}
      <div className={classes.root} style={{ width: "100%", marginBottom: "24px" }}>
        <SearchBar />
      </div>

      {/* Filters and New Vendor Button */}
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div>
          <Filters />
        </div>
        <div>
          <Gate capability="editRecords">
          <Button
            onClick={handleNewVendorFormOpen}
            size="small"
            variant="contained"
            color="secondary"
          >
            New Vendor
          </Button>
          </Gate>
        </div>
      </div>

      <EditableTable
        readOnly={true}
        canSave={false}
        canDelete={false}
        isLoading={vendorsLoading}
        currentActiveFilters={[vendorsStatusFilter]}
        canAddItems={false}
        saveOnTextboxLeave={true}
        defaultItemCount={10}
        tableTitle="Vendors"
        objectArray={toShowAll}
        fieldTitles={fieldTitles}
        fieldTypes={fieldTypes}
        fieldNames={fieldNames}
        canExport={true}
        showItemNumber={true}
        handleRowClickParentMethod={handleVendorRowClick}
        handlePreviewClickParentMethod={handleVendorEditFormOpen}
      />
    </LeftSidebar>
  );
};

export default VendorsPage;
